import type { Card } from '../../types'
import { motion } from 'framer-motion'
import { useEffect, useState } from 'react'
import { getCardPadding, getScaledIconSize } from '../../utils/gridLayout'
import { useThemeStore } from '../../store/themeStore'
import { useAudioPlayerStore } from '../../store/audioPlayerStore'

interface AudioCardProps {
  card: Card
  editMode: boolean
  cellSize: number
}

export default function AudioCard({ card, editMode, cellSize }: AudioCardProps) {
  const { theme } = useThemeStore()
  const rawAudioUrl = (card.content as any).audioUrl as string | undefined
  const soundcloudUrl = (card.content as any).audioSoundcloudUrl as string | undefined
  const rawCoverUrl = ((card.content as any).audioCoverUrl || (card.content as any).cover) as string | undefined
  const audioTitle = ((card.content as any).audioTitle || (card.content as any).name) as string | undefined
  const audioSubtext = ((card.content as any).audioSubtext || (card.content as any).subtext) as string | undefined
  const showTextOnCard = (card.content as any).audioShowTextOnCard !== false

  // Demo assets for local testing (development mode only)
  const DEMO_AUDIO_URL = 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-1.mp3'
  const DEMO_COVER_URL = 'https://picsum.photos/400/400?random=audio'
  const isDev = import.meta.env.DEV

  const hasSoundcloud = Boolean((soundcloudUrl || '').trim().match(/(^https?:\/\/)?((www|m)\.)?(soundcloud\.com|on\.soundcloud\.com)\//i))
  const audioUrl = (hasSoundcloud ? soundcloudUrl : undefined) || rawAudioUrl || (isDev ? DEMO_AUDIO_URL : undefined)
  const audioCoverUrl = rawCoverUrl || (isDev ? DEMO_COVER_URL : undefined)

  const { queue, activeIndex, wantedPlaying, isPlaying, toggleTrackByCardId } = useAudioPlayerStore()
  const [isHovered, setIsHovered] = useState(false)

  const padding = getCardPadding(cellSize)
  const iconSize = getScaledIconSize(cellSize, 28)
  const soundcloudIconSize = 24
  const soundcloudInsetRight = Math.max(8, Math.round(padding * 0.6))
  const soundcloudInsetTop = Math.max(3, Math.round(padding * 0.28))

  const togglePlay = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!audioUrl) return
    toggleTrackByCardId(card.id)
  }

  const activeTrack = activeIndex >= 0 ? queue[activeIndex] : null
  const isCardPlaying = activeTrack?.cardId === card.id && isPlaying
  const isCardLoadingRaw = activeTrack?.cardId === card.id && wantedPlaying && !isPlaying
  const [showLoading, setShowLoading] = useState(false)

  const PlayIcon = ({ size }: { size: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" className="pointer-events-none">
      <path d="M8 5v14l11-7z" fill="currentColor" />
    </svg>
  )

  const PauseIcon = ({ size }: { size: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" className="pointer-events-none">
      <rect x="6" y="5" width="4" height="14" fill="currentColor" />
      <rect x="14" y="5" width="4" height="14" fill="currentColor" />
    </svg>
  )

  const LoadingIcon = ({ size }: { size: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" className="pointer-events-none animate-spin">
      <circle cx="12" cy="12" r="8" fill="none" stroke="currentColor" strokeOpacity="0.24" strokeWidth="2.2" />
      <path d="M12 4a8 8 0 0 1 8 8" fill="none" stroke="currentColor" strokeLinecap="round" strokeWidth="2.2" />
    </svg>
  )

  useEffect(() => {
    if (!isCardLoadingRaw) {
      setShowLoading(false)
      return
    }

    const timer = window.setTimeout(() => {
      setShowLoading(true)
    }, 180)

    return () => window.clearTimeout(timer)
  }, [isCardLoadingRaw])

  const isCardLoading = isCardLoadingRaw && showLoading

  const SoundCloudIcon = ({ size }: { size: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <path d="M23.999 14.165c-.052 1.796-1.612 3.169-3.4 3.169h-8.18a.68.68 0 0 1-.675-.683V7.862a.747.747 0 0 1 .452-.724s.75-.513 2.333-.513a5.364 5.364 0 0 1 2.763.755 5.433 5.433 0 0 1 2.57 3.54c.282-.08.574-.121.868-.12.884 0 1.73.358 2.347.992s.948 1.49.922 2.373ZM10.721 8.421c.247 2.98.427 5.697 0 8.672a.264.264 0 0 1-.53 0c-.395-2.946-.22-5.718 0-8.672a.264.264 0 0 1 .53 0ZM9.072 9.448c.285 2.659.37 4.986-.006 7.655a.277.277 0 0 1-.55 0c-.331-2.63-.256-5.02 0-7.655a.277.277 0 0 1 .556 0Zm-1.663-.257c.27 2.726.39 5.171 0 7.904a.266.266 0 0 1-.532 0c-.38-2.69-.257-5.21 0-7.904a.266.266 0 0 1 .532 0Zm-1.647.77a26.108 26.108 0 0 1-.008 7.147.272.272 0 0 1-.542 0 27.955 27.955 0 0 1 0-7.147.275.275 0 0 1 .55 0Zm-1.67 1.769c.421 1.865.228 3.5-.029 5.388a.257.257 0 0 1-.514 0c-.21-1.858-.398-3.549 0-5.389a.272.272 0 0 1 .543 0Zm-1.655-.273c.388 1.897.26 3.508-.01 5.412-.026.28-.514.283-.54 0-.244-1.878-.347-3.54-.01-5.412a.283.283 0 0 1 .56 0Zm-1.668.911c.4 1.268.257 2.292-.026 3.572a.257.257 0 0 1-.514 0c-.241-1.262-.354-2.312-.023-3.572a.283.283 0 0 1 .563 0Z" fill="currentColor" />
    </svg>
  )

  const gridW = card.grid_width
  const gridH = Math.max(1, card.grid_height)
  const aspect = gridW / gridH

  const cardPxH = gridH * cellSize
  const hasTitle = (audioTitle || '').trim().length > 0
  const hasSubtext = (audioSubtext || '').trim().length > 0
  const hasCardTitle = showTextOnCard && hasTitle
  const hasCardSubtext = showTextOnCard && hasSubtext

  type Layout = 'wide' | 'square' | 'portrait'

  const layout: Layout = (() => {
    if (aspect >= 1.8) {
      if (!hasCardTitle && !hasCardSubtext) return 'square'
      return 'wide'
    }
    if (aspect >= 0.8 && aspect <= 1.25) return 'square'
    return 'portrait'
  })()

  if (!audioUrl) {
    return (
      <motion.div
        className="w-full h-full theme-card theme-card-hover overflow-hidden flex items-center justify-center"
        whileHover={{ borderColor: 'rgba(0,0,0,0.25)' }}
        style={{ padding: `${padding}px` }}
      />
    )
  }

  const isMonoTheme = (theme === 'neo' || theme === 'wood') && (card.content as any).audioFilter === 'theme'
  const soundcloudLink = hasSoundcloud && soundcloudUrl ? (/^https?:\/\//i.test(soundcloudUrl) ? soundcloudUrl : `https://${soundcloudUrl}`) : ''

  const renderSoundCloudLink = () =>
    soundcloudLink && !editMode ? (
      <a
        href={soundcloudLink}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        className="absolute z-30 transition-opacity hover:opacity-100"
        style={{
          top: `${soundcloudInsetTop}px`,
          right: `${soundcloudInsetRight}px`,
          color: '#ffffff',
          opacity: 0.94,
          mixBlendMode: 'exclusion',
        }}
        aria-label="Open on SoundCloud"
      >
        <SoundCloudIcon size={soundcloudIconSize} />
      </a>
    ) : null

  const renderCover = (className = '', style?: React.CSSProperties) => (
    <div className={`relative overflow-hidden ${className}`} style={{ ...style, borderRadius: 'var(--card-radius, 0px)' }}>
      {audioCoverUrl ? (
        <>
          <img src={audioCoverUrl} alt="" className={`w-full h-full object-cover ${isMonoTheme ? 'grayscale opacity-90' : ''}`} />
          {isMonoTheme && (
            <div className="absolute inset-0 pointer-events-none" style={{ backgroundColor: 'var(--card-bg)', mixBlendMode: 'multiply' }} />
          )}
        </>
      ) : (
        <div className="w-full h-full bg-[var(--app-bg)]/20" />
      )}
      <button
        type="button"
        onClick={togglePlay}
        className="absolute inset-0 z-10 flex items-center justify-center transition-colors hover:bg-black/10"
        style={{ backgroundColor: 'rgba(0,0,0,0.15)', color: 'rgba(255,255,255,0.95)' }}
        aria-label={isCardLoading ? 'Loading track' : isCardPlaying ? 'Pause track' : 'Play track'}
      >
        {isCardLoading ? <LoadingIcon size={iconSize * 1.4} /> : isCardPlaying ? <PauseIcon size={iconSize * 1.5} /> : <PlayIcon size={iconSize * 1.5} />}
      </button>
    </div>
  )

  const renderTextBlock = (titleSize: number, subtextSize: number, centered = false) => (
    <div className={`flex min-w-0 max-w-full flex-col justify-center overflow-hidden ${centered ? 'items-center text-center' : ''}`}>
      {hasCardTitle && (
        <p className="w-full max-w-full overflow-hidden text-ellipsis whitespace-nowrap font-serif font-bold leading-tight" style={{ fontSize: `${titleSize}px` }}>
          {audioTitle}
        </p>
      )}
      {hasCardSubtext && (
        <p className="w-full max-w-full overflow-hidden text-ellipsis whitespace-nowrap font-serif opacity-70" style={{ fontSize: `${subtextSize}px`, marginTop: '2px' }}>
          {audioSubtext}
        </p>
      )}
    </div>
  )

  if (layout === 'wide') {
    const cardPxW = gridW * cellSize
    const topPaddingY = Math.max(4, padding * 0.45)
    const bottomPaddingY = Math.max(8, padding * 0.95)
    const innerH = cardPxH - topPaddingY - bottomPaddingY

    // Cap cover size so it doesn't squish everything vertically
    const maxCoverSize = innerH
    const coverSize = Math.max(32, Math.min(80, cardPxH * 0.55, maxCoverSize))

    // Scale text based on card width
    const titleSize = Math.max(13, Math.min(17, cardPxW * 0.032, cardPxH * 0.13))
    const subtextSize = Math.max(9, titleSize * 0.75)

    return (
      <motion.div
        className="relative w-full h-full theme-card theme-card-hover overflow-hidden flex items-center"
        whileHover={{ borderColor: 'rgba(0,0,0,0.25)' }}
        style={{ padding: `${topPaddingY}px ${padding}px ${bottomPaddingY}px ${padding}px`, transform: 'translateZ(0)' }}
      >
        {renderSoundCloudLink()}
        <div className="flex w-full items-center min-h-0" style={{ gap: `${padding * 0.75}px` }}>
          {renderCover('flex-shrink-0', { width: `${coverSize}px`, height: `${coverSize}px` })}
          <div className="flex flex-1 min-w-0 basis-0 items-center justify-center overflow-hidden">
            {renderTextBlock(titleSize, subtextSize, true)}
          </div>
        </div>
      </motion.div>
    )
  }

  if (layout === 'square') {
    return (
      <motion.div
        className="relative w-full h-full theme-card theme-card-hover overflow-hidden"
        whileHover={{ borderColor: 'rgba(0,0,0,0.25)' }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{ transform: 'translateZ(0)' }}
      >
        {renderSoundCloudLink()}
        <div className="w-full h-full relative" style={{ overflow: 'hidden' }}>
          {audioCoverUrl ? (
            <>
              <img src={audioCoverUrl} alt="" className={`w-full h-full object-cover ${isMonoTheme ? 'grayscale opacity-90' : ''}`} />
              {isMonoTheme && (
                <div className="absolute inset-0 pointer-events-none" style={{ backgroundColor: 'var(--card-bg)', mixBlendMode: 'multiply' }} />
              )}
            </>
          ) : (
            <div className="w-full h-full bg-[var(--app-bg)]/20" />
          )}


          <button
            type="button"
            onClick={togglePlay}
            className={`absolute inset-0 z-10 flex items-center justify-center transition-colors duration-300 ${isHovered || isCardPlaying || isCardLoading ? 'bg-black/30' : 'bg-transparent'
              }`}
            style={{ color: isHovered || isCardPlaying || isCardLoading ? 'rgba(255,255,255,0.95)' : 'transparent' }}
            aria-label={isCardLoading ? 'Loading track' : isCardPlaying ? 'Pause track' : 'Play track'}
          >
            {isCardLoading ? <LoadingIcon size={iconSize * 1.65} /> : isCardPlaying ? <PauseIcon size={iconSize * 1.8} /> : <PlayIcon size={iconSize * 1.8} />}
          </button>
        </div>

        {hasCardTitle && (
          <div
            className={`absolute bottom-0 left-0 w-full p-3 bg-gradient-to-t from-black/80 to-transparent transition-opacity duration-300 ${isHovered || isCardPlaying ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
              }`}
          >
            <p className="font-serif font-bold text-white truncate" style={{ fontSize: `${Math.max(12, iconSize * 0.55)}px` }}>
              {audioTitle}
            </p>
          </div>
        )}
      </motion.div>
    )
  }

  const showText = showTextOnCard && cardPxH >= 140 && (hasTitle || hasSubtext)
  const titleSize = Math.max(14, Math.min(20, cardPxH * 0.08))
  const subtextSize = Math.max(12, Math.min(15, cardPxH * 0.06))

  return (
    <motion.div
      className="relative w-full h-full theme-card theme-card-hover overflow-hidden flex flex-col"
      whileHover={{ borderColor: 'rgba(0,0,0,0.25)' }}
      style={{ transform: 'translateZ(0)' }}
    >
      {renderSoundCloudLink()}
      <div className="relative flex-1 min-h-0 w-full" style={{ overflow: 'hidden' }}>
        {audioCoverUrl ? (
          <>
            <img src={audioCoverUrl} alt="" className={`w-full h-full object-cover ${isMonoTheme ? 'grayscale opacity-90' : ''}`} />
            {isMonoTheme && (
              <div className="absolute inset-0 pointer-events-none" style={{ backgroundColor: 'var(--card-bg)', mixBlendMode: 'multiply' }} />
            )}
          </>
        ) : (
          <div className="w-full h-full bg-[var(--app-bg)]/20" />
        )}
        <button
          type="button"
          onClick={togglePlay}
          className="absolute inset-0 z-10 flex items-center justify-center transition-colors hover:bg-black/10"
          style={{ backgroundColor: 'rgba(0,0,0,0.15)', color: 'rgba(255,255,255,0.95)' }}
          aria-label={isCardLoading ? 'Loading track' : isCardPlaying ? 'Pause track' : 'Play track'}
        >
          {isCardLoading ? <LoadingIcon size={iconSize * 1.4} /> : isCardPlaying ? <PauseIcon size={iconSize * 1.5} /> : <PlayIcon size={iconSize * 1.5} />}
        </button>
      </div>

      {showText ? (
        <div className="flex flex-col flex-shrink-0 w-full" style={{ padding: `${padding * 0.55}px ${padding}px ${padding * 0.85}px ${padding}px` }}>
          {renderTextBlock(titleSize, subtextSize)}
        </div>
      ) : null}
    </motion.div>
  )
}
